import type { ReactNode } from 'react';
import type React from 'react';

/* eslint-disable react/jsx-props-no-spreading */
import { MemberPriceSplash, PriceTag } from '@coop/components';
import classNames from 'classnames';

import A11yTextWrapper, {
    type A11yTextType,
} from '../../../common/components/A11yTextWrapper/A11yTextWrapper';
import { useIsId26Theme } from '../../hooks/useIsId26Theme';
import styles from './ProductPriceLabel.module.scss';

type BaseLabelProps = {
    children?: ReactNode;
    currency?: boolean;
    unit?: string;
    a11yText?: string;
    a11yType: A11yTextType;
};

export type LabelTheme =
    | NonNullable<React.ComponentProps<typeof PriceTag.Container>['variant']>
    | 'legacyRed';

export type MemberLabelSize = Exclude<
    React.ComponentProps<typeof MemberPriceSplash>['size'],
    'large'
>;

type PriceTagVariant = 'attention' | 'ghost';

const Postfix = (props: { currency?: boolean; unit?: string }) => {
    if (!props.currency && !props.unit) return null;

    if (props.currency) {
        return <PriceTag.Text size="small">{props.unit ? `kr/${props.unit}` : 'kr'}</PriceTag.Text>;
    }

    return <PriceTag.Text size="small">/{props.unit}</PriceTag.Text>;
};

const PriceTagLabel = (
    props: BaseLabelProps & { variant: PriceTagVariant; className?: string },
) => (
    <A11yTextWrapper
        a11yText={props.a11yText}
        a11yType={props.a11yType}
        className={props.className}
        containerClassName={styles.A11yTextWrapper}
    >
        <PriceTag.Container variant={props.variant}>
            <PriceTag.Text size="medium">
                <span className={styles.PriceTagTextContent}>{props.children}</span>
            </PriceTag.Text>
            <Postfix currency={props.currency} unit={props.unit} />
        </PriceTag.Container>
    </A11yTextWrapper>
);

const Label = (props: {
    children: string;
    theme: LabelTheme;
    ariaHidden?: boolean;
    size: 'small' | 'medium';
}) => {
    if (props.theme === 'legacyRed') {
        return (
            <div
                className={classNames(styles.Label, styles.RedBgColor, {
                    [styles.SmallLabel]: props.size === 'small',
                })}
                aria-hidden={props.ariaHidden}
            >
                {props.children}
            </div>
        );
    }

    return (
        <PriceTag.Container variant={props.theme} aria-hidden={props.ariaHidden}>
            <PriceTag.Text size={props.size}>{props.children}</PriceTag.Text>
        </PriceTag.Container>
    );
};

const MemberPriceGroup = (props: { children: ReactNode; memberLabelSize?: MemberLabelSize }) => {
    const isId26 = useIsId26Theme();
    const memberLabelSize = props.memberLabelSize ?? 'medium';

    if (isId26) {
        return (
            <div className={styles.MemberPriceStack}>
                <MemberPriceSplash aria-hidden size={memberLabelSize} />
                <div className={styles.PriceTagWrap}>{props.children}</div>
            </div>
        );
    }

    return (
        <div className={styles.LabelGroup}>
            <Label ariaHidden size="small" theme="primary">
                MEDLEMSPRIS
            </Label>
            <div className={styles.PriceTagWrap}>{props.children}</div>
        </div>
    );
};

const RegularLabel = (props: BaseLabelProps & { additionalClasses?: string }) => (
    <PriceTagLabel {...props} className={props.additionalClasses} variant="ghost" />
);

const DiscountLabel = (props: BaseLabelProps) => <PriceTagLabel {...props} variant="attention" />;

const LabelGroup = (props: { children: ReactNode }) => (
    <div className={styles.LabelGroup}>{props.children}</div>
);

const MaybeMemberDiscountLabel = (
    props: BaseLabelProps & {
        isMemberPrice: boolean;
        labelSize?: MemberLabelSize;
        secondPrice?: ReactNode;
    },
) => {
    const { secondPrice, labelSize, isMemberPrice, ...discountProps } = props;

    if (isMemberPrice) {
        return (
            <MemberPriceGroup memberLabelSize={labelSize}>
                <DiscountLabel {...discountProps} />
                {secondPrice}
            </MemberPriceGroup>
        );
    }

    if (!secondPrice) {
        return <DiscountLabel {...discountProps} />;
    }

    return (
        <>
            <DiscountLabel {...discountProps} />
            {secondPrice}
        </>
    );
};

const MaybeLowerPriceLabel = (props: BaseLabelProps & { displayLowerPrice: boolean }) => {
    if (props.displayLowerPrice) {
        return (
            <LabelGroup>
                <Label ariaHidden size="small" theme="legacyRed">
                    LÄGRE PRIS
                </Label>
                <RegularLabel {...props} />
            </LabelGroup>
        );
    }

    return <RegularLabel {...props} />;
};

const ProductPriceLabel = {
    Regular: RegularLabel,
    Discount: DiscountLabel,
    MaybeMemberDiscount: MaybeMemberDiscountLabel,
    MaybeLowerPrice: MaybeLowerPriceLabel,
    LabelGroup,
    Label,
    MemberPriceGroup,
};

export default ProductPriceLabel;
